import { createRouter, createWebHistory } from 'vue-router'

import { useAppStore } from '@/stores/app'
import { isAuthenticated } from '@/libs/auth'
import { UserRole } from '@/enums/domain'

const ADMIN = [UserRole.SuperAdmin, UserRole.ProbeAdmin]
const SA = [UserRole.SuperAdmin]

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition)         return savedPosition
        if (from.path === to.path) return {}
        return { top: 0 }
    },
    routes: [
        { path: '/', redirect: '/dashboard' },
        {
            path: '/login',
            name: 'login',
            component: () => import('@/views/Login.vue'),
            meta: { layout: 'Blank', requiresAuth: false },
        },
        {
            path: '/dashboard',
            name: 'dashboard',
            component: () => import('@/views/dashboard/Dashboard.vue'),
            meta: { requiresAuth: true },
        },
        {
            path: '/rezervacije',
            name: 'reservations',
            component: () => import('@/views/reservation/Reservations.vue'),
            meta: { requiresAuth: true },
        },
        {
            path: '/naprave',
            name: 'devices',
            component: () => import('@/views/device/Devices.vue'),
            meta: { requiresAuth: true, roles: ADMIN },
        },
        {
            path: '/sonde',
            name: 'probes',
            component: () => import('@/views/probe/Probes.vue'),
            meta: { requiresAuth: true, roles: ADMIN },
        },
        {
            path: '/uporabniki',
            name: 'users',
            component: () => import('@/views/user/Users.vue'),
            meta: { requiresAuth: true, roles: SA },
        },
        {
            path: '/skupine',
            name: 'groups',
            component: () => import('@/views/group/Groups.vue'),
            meta: { requiresAuth: true, roles: SA },
        },
        {
            path: '/stranke',
            name: 'clients',
            component: () => import('@/views/client/Clients.vue'),
            meta: { requiresAuth: true, roles: SA },
        },
        {
            path: '/kvote',
            name: 'quotas',
            component: () => import('@/views/quota/Quotas.vue'),
            meta: { requiresAuth: true, roles: ADMIN },
        },
        {
            path: '/porocila',
            name: 'reports',
            component: () => import('@/views/report/Reports.vue'),
            // v1.16: also open to a role-`user` account with the report grant
            meta: { requiresAuth: true, roles: [...ADMIN, UserRole.Reporter], reportsGrant: true },
        },
        {
            path: '/potrditve',
            name: 'confirmations',
            component: () => import('@/views/confirmation/Confirmations.vue'),
            meta: { requiresAuth: true, roles: ADMIN },
        },
        {
            path: '/menjave-sond',
            name: 'probe-plan',
            component: () => import('@/views/probeplan/ProbePlan.vue'),
            meta: { requiresAuth: true, roles: ADMIN },
        },
        {
            path: '/nedelovanja',
            name: 'downtimes',
            component: () => import('@/views/downtime/Downtimes.vue'),
            meta: { requiresAuth: true, roles: ADMIN },
        },
        {
            path: '/obvestila',
            name: 'notifications',
            component: () => import('@/views/notification/Notifications.vue'),
            meta: { requiresAuth: true },
        },
        {
            path: '/nastavitve',
            name: 'settings',
            component: () => import('@/views/settings/Settings.vue'),
            meta: { requiresAuth: true },
        },
        {
            path: '/profil',
            name: 'profile',
            component: () => import('@/views/profile/Profile.vue'),
            meta: { requiresAuth: true },
        },
        {
            path: '/dostop-zavrnjen',
            name: 'access-denied',
            component: () => import('@/views/AccessDenied.vue'),
            meta: { requiresAuth: true },
        },
        {
            path: '/:pathMatch(.*)*',
            name: 'not-found',
            component: () => import('@/views/NotFound.vue'),
            meta: { requiresAuth: true },
        },
    ],
})

// v1.16: the reporter role only reads reports — everything else (dashboard, booking,
// administration) sends it to Reports; its inbox, settings and profile stay open.
const REPORTER_PAGES = ['reports', 'notifications', 'settings', 'profile', 'access-denied', 'login']
export const reporterMayOpen = (name: unknown): boolean => REPORTER_PAGES.includes(name as string)

router.beforeEach((to, _from, next) => {
    const appStore = useAppStore()
    const loggedIn = isAuthenticated()

    if (loggedIn && to.name === 'login') return next({ name: 'dashboard' })
    if (to.meta.requiresAuth && !loggedIn) {
        appStore.setLogout()
        return next({ name: 'login' })
    }

    if (loggedIn && appStore.userData?.role === UserRole.Reporter && !reporterMayOpen(to.name)) {
        return next({ name: 'reports' })
    }

    const roles = to.meta.roles as string[] | undefined
    if (roles && loggedIn) {
        const role = appStore.userData?.role
        const granted = !!to.meta.reportsGrant && !!appStore.userData?.can_view_reports
        if (role && !roles.includes(role) && !granted) {
            return next({ name: 'access-denied' })
        }
    }

    next()
})

export default router
